import {
  Component,
  Input,
  Output,
  ViewEncapsulation,
  EventEmitter,
} from "@angular/core";
import { Subscription } from "rxjs";
import { VAIconRegistryService, close } from "@va/core-library/icons";

@Component({
  selector: "app-shortcuts-modal",
  templateUrl: "./shortcuts-modal.component.html",
  styleUrls: ["./shortcuts-modal.component.scss"],
  encapsulation: ViewEncapsulation.None,
  host: {
    "(document:keydown)": "handleKeyboardEvent($event)",
  },
})
export class ShortcutsModalComponent {
  /* vars for selectors and switch */
  leftSiderTrigger: HTMLElement;
  rightSiderTrigger: HTMLElement;
  formulabar: HTMLElement;
  inspector: HTMLElement;
  lineItemSerch: HTMLElement;
  globalSearch: HTMLElement;
  periods: any;
  firstEstimatedFiscal: any;
  firstEstimatedYear: any;

  shortcutMap = [
    { symbol: "L", text: "Left Panel: See Company Views" },
    { symbol: "R", text: "Right Panel: Analyze Company Data" },
    { symbol: "F", text: "Formula Bar: Inspect Calculations" },
    { symbol: "I", text: "Inspector: Analyze Current Line Item" },
    { symbol: "A", text: "Annuals: See Brokers for Current Year" },
    { symbol: "Q", text: "Quarters: See Brokers for Current Quarter" },
    { symbol: "S", text: "Line Item Search" },
    {
      symbol: "G",
      text: "Global Search: Search Other Companies, Industries and Analysts",
    },
  ];

  ACCEPT_KEYS = ["/", "?", "l", "r", "f", "i", "a", "q", "s", "g"];
  cut = "";
  navigationSubscription: Subscription;

  @Input() isModalOpen: boolean;
  @Input() showNewFbar: boolean;
  @Output() onClose: EventEmitter<boolean> = new EventEmitter();

  constructor(private iconRegistry: VAIconRegistryService) {
    this.iconRegistry.registerIcons([close]);
  }

  handleKeyboardEvent(event: KeyboardEvent) {
    const key = event.key.toLowerCase();
    if (document.activeElement.tagName === "INPUT") {
      //if we are focused on an input element i.e user is typing
      //just return and let the user type in peace
      return;
    }

    if (event.ctrlKey || (event.shiftKey && event.key !== "?")) {
      //combination keys shouldn't trigger shortcuts
      return;
    }

    //prevent default(enter text, submit form,...) and make the key down perform following events
    if (this.ACCEPT_KEYS.includes(key)) {
      event.preventDefault();
    }
    switch (key) {
      case "/":
      case "?":
        this.handleModalState();
        break;

      case "l":
        this.leftSiderTrigger = document.querySelector(
          ".leftSider .ant-layout-sider-zero-width-trigger",
        );
        if (this.leftSiderTrigger) {
          this.leftSiderTrigger.click();
        }
        break;

      case "r":
        this.rightSiderTrigger = document.querySelector(
          ".rightSider .ant-layout-sider-zero-width-trigger",
        );
        if (this.rightSiderTrigger) {
          this.rightSiderTrigger.click();
        }
        break;

      case "f":
        if (this.showNewFbar) {
          this.formulabar = document.querySelector(
            ".formula-bar .mat-tree-node",
          );
        } else if (!this.showNewFbar) {
          this.formulabar = document.querySelector(
            "#formulabar nz-tree-node-switcher",
          );
        }
        if (this.formulabar) {
          this.formulabar.click();
        }
        break;

      case "i":
        this.inspector = document.querySelector("#tblIns div");
        if (this.inspector) {
          this.inspector.click();
        }
        break;

      case "s":
        this.lineItemSerch = document.querySelector("#search-input input");
        if (this.lineItemSerch) {
          this.lineItemSerch.focus();
        }
        break;

      case "a":
        if (
          window.MEData &&
          window.MEData.panels &&
          window.MEData.panels.length
        ) {
          this.periods = this.getDataFrom(window.MEData.panels, "periods");
          this.firstEstimatedYear =
            this.periods.first_estimated_year.toLowerCase();
          this.toggleExpansionState(this.firstEstimatedYear);
        }
        break;
      case "q":
        if (
          window.MEData &&
          window.MEData.panels &&
          window.MEData.panels.length
        ) {
          this.periods = this.getDataFrom(window.MEData.panels, "periods");
          this.firstEstimatedFiscal =
            this.periods.first_estimated_fiscal.toLowerCase();
          this.toggleExpansionState(this.firstEstimatedFiscal);
        }
        break;

      case "g":
        this.globalSearch = document.querySelector("#search-input");
        if (this.globalSearch) {
          this.globalSearch.focus();
        }
        break;

      default:
        break;
    }
  }

  handleModalState() {
    if (this.isModalOpen) {
      this.onClose.emit(false);
    } else {
      this.onClose.emit(true);
    }
  }

  toggleExpansionState(estimatedPeriod, trycount = 1) {
    const headerCols: HTMLElement = document.querySelector(
      ".datagrid-view2 .datagrid-header",
    );
    let focusedSource = window.gFocusSource.toLowerCase();
    focusedSource = focusedSource.replace("+", "plus");

    let revision = this.getDataFrom(
      window.MEData.column_dimensions,
      "revisions",
    );
    revision = revision ? "current" : "";

    if (!headerCols || !focusedSource) {
      return;
    }

    const fieldParam = estimatedPeriod + revision + focusedSource;
    const firstEstParam = `fy-${estimatedPeriod.match(/\d{4}/)[0]}`;

    const firstEstSourceCell: HTMLElement = headerCols.querySelector(
      `[field="${fieldParam}" i]`,
    );
    const firstEstYearCell: HTMLElement = headerCols.querySelector(
      `[field="${firstEstParam}" i]`,
    );
    const isExpanded =
      firstEstYearCell.querySelector("img") &&
      firstEstYearCell.querySelector("img").src.includes("collapse");

    if (estimatedPeriod.includes("q") && !isExpanded) {
      //if estimated period has q -- for quaterly but the annual period is not expanded
      //then first expand the annual period
      this.performClick(firstEstYearCell);
    }

    if (firstEstSourceCell) {
      this.performClick(firstEstSourceCell);
    } else if (trycount <= 10) {
      //grid might still be loading. try after a sec
      setTimeout(
        () => this.toggleExpansionState(estimatedPeriod, trycount++),
        1000,
      );
    }
  }

  performClick(elememt: HTMLElement) {
    const imgElement = elememt.querySelector("img");
    if (!imgElement) {
      return;
    }
    imgElement.click();
  }

  getDataFrom(dataList, name) {
    return dataList.find(
      (data) => data.name.toLowerCase() === name.toLowerCase(),
    );
  }
}
